import { beginMessage } from '../message';
import { MessageTag } from '../messageTags';
import { RoomName } from '../enums';
import type { BinaryReader } from '../BinaryReader';

/**
 * Lobby / main-menu codec.
 * Mirrors PlayerService.SendMain (MainMenu payload) and the Network.cs lobby switch.
 */

/** C2S: request the lobby state (empty body). Server replies MainMenu(24) — or GameInfo if mid-game. */
export function encodeStatus(): Uint8Array {
  return beginMessage(MessageTag.Status).toUint8Array();
}

/** C2S Logout(64): end the authenticated server session (empty body). */
export function encodeLogout(): Uint8Array {
  return beginMessage(MessageTag.Logout).toUint8Array();
}

/**
 * C2S ReadMessage(7): acknowledge the lobby system/welcome message so the server marks it read
 * (empty body — the server uses the player context).
 *
 * Parity note: the Unity *client* (AdminMsg.cs) actually sends BackLink(62)+"readmsg" on OK, but the
 * server has NO BackLink handler — that packet is a no-op. The handler that truly clears the message
 * is ReadMessage(7) (MessageProcessModule.cs): it deletes the per-user UserMessage metadata, or
 * records the SystemMessage read-hash, so PlayerService.GetUserWelcomeMessage stops returning it on
 * the next MainMenu. We send ReadMessage so the acknowledge actually takes effect on the server.
 */
export function encodeReadMessage(): Uint8Array {
  return beginMessage(MessageTag.ReadMessage).toUint8Array();
}

export interface MainMenuState {
  onlineCount: number;
  balance: bigint;
  isWaiting: boolean;
  adminMessage: string;
  avatar: number;
}

/** S2C MainMenu(24): int onlineCount, long balance, bool isWaiting, string adminMessage, int avatar. */
export function decodeMainMenu(r: BinaryReader): MainMenuState {
  return {
    onlineCount: r.getInt(),
    balance: r.getLong(),
    isWaiting: r.getBool(),
    adminMessage: r.getString(),
    avatar: r.getInt(),
  };
}

/** S2C ChangeRoom(25): a single short RoomName telling the client which scene to show. */
export function decodeChangeRoom(r: BinaryReader): RoomName {
  return r.getShort() as RoomName;
}

/** C2S: request the online-player list (empty body). */
export function encodeOnlineListRequest(): Uint8Array {
  return beginMessage(MessageTag.OnlineList).toUint8Array();
}

/** C2S Telegram(6): request the official channel URL (empty body — the server replies Telegram + url). */
export function encodeTelegramRequest(): Uint8Array {
  return beginMessage(MessageTag.Telegram).toUint8Array();
}

/** S2C Telegram(6): a single string — the channel/bot URL to open (Unity does Application.OpenURL). */
export function decodeTelegram(r: BinaryReader): string {
  return r.getString();
}

/** S2C OnlineList(8): a ushort-prefixed string[] of usernames. */
export function decodeOnlineList(r: BinaryReader): string[] {
  return r.getStringArray();
}
